import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import {
  Popover,
  Typography,
  message,
} from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { DateTime } from 'luxon';
import { TaskHelpers } from 'ontraccr-common';

import OnTraccrTimePicker from '../inputs/OnTraccrTimePicker';
import OnTraccrButton from '../buttons/OnTraccrButton';
import TimeTracking from '../../state/timeTracking';
import colors from '../../constants/Colors';

const { Text } = Typography;

export default function EditClockStartTime({
  task = {},
  user = {},
  canEdit = false,
  otherTasks = [],
}) {
  const dispatch = useDispatch();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pendingStart, setPendingStart] = useState(null);
  const [saving, setSaving] = useState(false);

  const {
    id: taskId,
    startTime,
    timezone,
    date,
  } = task;

  const formattedStart = useMemo(() => {
    if (!startTime) return null;
    const zone = timezone || DateTime.local().zoneName;
    return DateTime.fromMillis(startTime, { zone }).toFormat('h:mm a');
  }, [startTime, timezone]);

  const onSave = async () => {
    if (!pendingStart || pendingStart === startTime) {
      setPickerOpen(false);
      return;
    }
    const now = DateTime.local().toMillis();
    if (pendingStart > now) {
      message.error('Cannot set clock in time in the future');
      return;
    }
    const conflict = TaskHelpers.hasTaskConflict({
      currentTasks: otherTasks,
      newTasks: [{
        ...task,
        startTime: pendingStart,
        originalStart: pendingStart,
        endTime: now,
      }],
    });
    if (conflict) {
      message.error('Clock in time overlaps another time card');
      return;
    }

    setSaving(true);
    const result = await dispatch(TimeTracking.editClockStart(
      taskId,
      { startTime: pendingStart },
      user.id,
    ));
    setSaving(false);
    if (result) {
      setPickerOpen(false);
    }
  };

  if (!startTime) return null;

  const timeText = (
    <Text
      style={{
        fontFamily: 'roboto-light',
        fontSize: 12,
        color: colors.ONTRACCR_OPACITY_GRAY,
        cursor: canEdit ? 'pointer' : 'default',
      }}
    >
      {formattedStart}
      {canEdit && (
        <EditOutlined style={{ marginLeft: 6, fontSize: 12 }} />
      )}
    </Text>
  );

  if (!canEdit) return timeText;

  return (
    <Popover
      trigger="click"
      visible={pickerOpen}
      onVisibleChange={(open) => {
        setPickerOpen(open);
        if (open) setPendingStart(startTime);
      }}
      placement="bottom"
      content={(
        <div
          style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
          onClick={(e) => e.stopPropagation()}
        >
          <Text style={{ fontSize: 12 }}>Edit clock in time</Text>
          <OnTraccrTimePicker
            date={date || DateTime.fromMillis(startTime).toSQLDate()}
            timezone={timezone || DateTime.local().zoneName}
            format="h:mm a"
            value={pendingStart || startTime}
            onChange={setPendingStart}
            allowClear={false}
            use12Hours
          />
          <OnTraccrButton
            title="Save"
            type="primary"
            onClick={onSave}
            loading={saving}
            style={{ width: '100%' }}
          />
        </div>
      )}
    >
      <span onClick={(e) => e.stopPropagation()}>
        {timeText}
      </span>
    </Popover>
  );
}

EditClockStartTime.propTypes = {
  task: PropTypes.shape({
    id: PropTypes.string,
    startTime: PropTypes.number,
    timezone: PropTypes.string,
    date: PropTypes.string,
  }),
  user: PropTypes.shape({
    id: PropTypes.string,
  }),
  canEdit: PropTypes.bool,
  otherTasks: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
  })),
};

EditClockStartTime.defaultProps = {
  task: {},
  user: {},
  canEdit: false,
  otherTasks: [],
};
